import { useMutation, useQueryClient } from 'react-query'
import { AIJobStatus, BrandKit } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { fetchBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { deleteBrandKit } from './brandKits/useDeleteBrandKit'
import { useCreateBrandKit, Options as CreateOptions, Variables } from 'hooks/queries/useCreateBrandKit'

interface Options extends CreateOptions {
  /**
   * Callback to be called on each poll interval. Useful for updating the UI
   * with the latest data.
   */
  onPollInterval?: (brandKit: BrandKit) => void
}

export const useCreateBrandKitSync = (options?: Options) => {
  const { mutateAsync: createBrandKit } = useCreateBrandKit()
  const workspaceId = useCurrentWorkspaceId()
  const { onPollInterval, ...restOptions } = options ?? {}
  const queryClient = useQueryClient()

  return useMutation<BrandKit, Error, Variables>(async (vars) => {
    const { sources, name, signal } = vars

    let brandKit = await createBrandKit({
      sources,
      name,
      signal,
    })

    let interval = 500
    do {
      const result = await fetchBrandKit({ id: brandKit.id, workspaceId })

      brandKit = result.brand_kit

      onPollInterval?.(brandKit)

      if (signal?.aborted && brandKit) {
        await deleteBrandKit(brandKit.id, workspaceId)
        throw new DOMException('The operation was aborted.', 'AbortError')
      }
      if (brandKit?.status === AIJobStatus.Failed) {
        throw new Error('Failed to populate brand kit')
      }

      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (![AIJobStatus.Failed, AIJobStatus.Expired, AIJobStatus.Successful].includes(brandKit?.status))

    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])

    return brandKit
  }, restOptions)
}
